import { IndianRupee, TrendingDown, TrendingUp } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MarketPosition, SalaryIntelligence } from '../../services/salaryIntelligenceService';

export interface SalaryIntelligenceCardProps {
  intel: SalaryIntelligence;
  compact?: boolean;
  className?: string;
}

function marketPositionKey(position: MarketPosition): string {
  return `dashboard.jobs.salaryIntel.marketPosition.${position}`;
}

function marketPositionFallback(position: MarketPosition): string {
  switch (position) {
    case 'well_above':
      return 'Well above average';
    case 'above_average':
      return 'Above average';
    case 'at_market':
      return 'At market';
    case 'below_average':
      return 'Below average';
    case 'well_below':
      return 'Well below average';
    default:
      return 'At market';
  }
}

const SalaryIntelligenceCard: React.FC<SalaryIntelligenceCardProps> = ({
  intel,
  compact = false,
  className = '',
}) => {
  const { t } = useTranslation();

  if (!intel.hasData) return null;

  const positionLabel = t(
    marketPositionKey(intel.marketPosition),
    marketPositionFallback(intel.marketPosition),
  );

  const underpaidInsight =
    intel.underpaidPercent != null
      ? t('dashboard.jobs.salaryIntel.underpaidInsight', {
          defaultValue: 'Based on your experience, you may be underpaid by ~{{percent}}%.',
          percent: intel.underpaidPercent,
        })
      : null;

  if (compact) {
    return (
      <div className={`ds-salary-intel ds-salary-intel--compact ${className}`.trim()} aria-label={intel.estimatedLabel}>
        <IndianRupee className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span className="ds-salary-intel__compact-value t-num">{intel.estimatedLabel}</span>
        {intel.isEstimated ? (
          <span className="ds-salary-intel__estimate-tag">
            {t('dashboard.jobs.salaryIntel.estimatedTag', 'Est.')}
          </span>
        ) : null}
      </div>
    );
  }

  const positionTone =
    intel.marketPosition === 'well_above' || intel.marketPosition === 'above_average'
      ? 'positive'
      : intel.marketPosition === 'below_average' || intel.marketPosition === 'well_below'
        ? 'negative'
        : 'neutral';

  return (
    <section
      className={`ds-salary-intel ds-card ${className}`.trim()}
      aria-labelledby="salary-intel-title"
    >
      <header className="ds-salary-intel__header">
        <IndianRupee className="w-4 h-4 shrink-0" aria-hidden />
        <h3 id="salary-intel-title" className="ds-salary-intel__title">
          {t('dashboard.jobs.salaryIntel.title', 'Salary intelligence')}
        </h3>
        {intel.isEstimated ? (
          <span className="ds-salary-intel__estimate-tag">
            {t('dashboard.jobs.salaryIntel.estimatedTag', 'Est.')}
          </span>
        ) : null}
      </header>

      <dl className="ds-salary-intel__rows">
        <div className="ds-salary-intel__row">
          <dt>{t('dashboard.jobs.salaryIntel.estimatedSalary', 'Estimated salary')}</dt>
          <dd className="t-num">{intel.estimatedLabel}</dd>
        </div>

        {intel.expectedLabel ? (
          <div className="ds-salary-intel__row">
            <dt>{t('dashboard.jobs.salaryIntel.yourExpectedRange', 'Your expected range')}</dt>
            <dd className="t-num">{intel.expectedLabel}</dd>
          </div>
        ) : null}

        <div className="ds-salary-intel__row">
          <dt>{t('dashboard.jobs.salaryIntel.marketPositionLabel', 'Market position')}</dt>
          <dd>
            <span className={`ds-salary-intel__position ds-salary-intel__position--${positionTone}`}>
              {intel.marketPosition === 'above_average' || intel.marketPosition === 'well_above' ? (
                <TrendingUp className="w-3.5 h-3.5" aria-hidden />
              ) : intel.marketPosition === 'below_average' || intel.marketPosition === 'well_below' ? (
                <TrendingDown className="w-3.5 h-3.5" aria-hidden />
              ) : null}
              {positionLabel}
            </span>
          </dd>
        </div>
      </dl>

      {underpaidInsight ? (
        <p className="ds-salary-intel__insight" role="note">
          {underpaidInsight}
        </p>
      ) : null}
    </section>
  );
};

export default React.memo(SalaryIntelligenceCard);
